/**
 * Authentication Error Types
 * Defines error codes and types for authentication error handling
 */

/**
 * Authentication error codes returned by the API
 */
export enum AuthErrorCode {
  /** Invalid email or password */
  INVALID_CREDENTIALS = 'INVALID_CREDENTIALS',
  /** Account has been temporarily locked due to too many failed attempts */
  ACCOUNT_LOCKED = 'ACCOUNT_LOCKED',
  /** Account has been disabled by an administrator */
  ACCOUNT_DISABLED = 'ACCOUNT_DISABLED',
  /** Too many login attempts - must wait before retrying */
  RATE_LIMITED = 'RATE_LIMITED',
  /** Network connectivity issue */
  NETWORK_ERROR = 'NETWORK_ERROR',
  /** Server error occurred */
  SERVER_ERROR = 'SERVER_ERROR',
  /** User session has expired */
  SESSION_EXPIRED = 'SESSION_EXPIRED',
  /** Email address not verified */
  EMAIL_NOT_VERIFIED = 'EMAIL_NOT_VERIFIED',
}

/**
 * Authentication error with code and optional metadata
 */
export interface AuthError {
  /** Error code identifying the type of error */
  code: AuthErrorCode;
  /** Human-readable error message */
  message: string;
  /** Seconds until rate limit resets (for RATE_LIMITED errors) */
  retryAfter?: number;
  /** Minutes until account unlocks (for ACCOUNT_LOCKED errors) */
  lockoutRemaining?: number;
}

/**
 * Type guard to check if an error has an AuthErrorCode
 */
export const isAuthError = (error: unknown): error is AuthError => {
  if (!error || typeof error !== 'object') return false;
  const err = error as Record<string, unknown>;
  return (
    typeof err.code === 'string' &&
    Object.values(AuthErrorCode).includes(err.code as AuthErrorCode) &&
    typeof err.message === 'string'
  );
};

/**
 * Parse an API error response into an AuthError
 */
export const parseApiError = (error: unknown): AuthError => {
  // Check if it's already an AuthError
  if (isAuthError(error)) {
    return error;
  }

  // Handle Axios-style errors
  if (error && typeof error === 'object') {
    const axiosError = error as {
      response?: {
        data?: {
          code?: string;
          message?: string;
          retryAfter?: number;
          lockoutRemaining?: number;
        };
        status?: number;
      };
      message?: string;
      code?: string;
    };

    // Check for network errors (no response)
    if (axiosError.code === 'ERR_NETWORK' || axiosError.message === 'Network Error') {
      return {
        code: AuthErrorCode.NETWORK_ERROR,
        message: 'Unable to connect. Please check your internet connection.',
      };
    }

    // Check for API response with error details
    if (axiosError.response?.data) {
      const data = axiosError.response.data;
      const code = data.code as AuthErrorCode | undefined;

      // Map known error codes
      if (code && Object.values(AuthErrorCode).includes(code)) {
        return {
          code,
          message: data.message || getDefaultMessage(code),
          retryAfter: data.retryAfter,
          lockoutRemaining: data.lockoutRemaining,
        };
      }

      // Handle HTTP status codes
      const status = axiosError.response.status;
      if (status === 401) {
        return {
          code: AuthErrorCode.INVALID_CREDENTIALS,
          message: data.message || 'Invalid email or password. Please try again.',
        };
      }
      if (status === 429) {
        return {
          code: AuthErrorCode.RATE_LIMITED,
          message: data.message || 'Too many login attempts. Please try again later.',
          retryAfter: data.retryAfter,
        };
      }
      if (status === 403) {
        return {
          code: AuthErrorCode.ACCOUNT_DISABLED,
          message:
            data.message || 'Your account has been disabled. Please contact your administrator.',
        };
      }
      if (status && status >= 500) {
        return {
          code: AuthErrorCode.SERVER_ERROR,
          message: 'Something went wrong. Please try again later or contact support.',
        };
      }

      // Generic error with message from API
      if (data.message) {
        return {
          code: AuthErrorCode.SERVER_ERROR,
          message: data.message,
        };
      }
    }
  }

  // Fallback to generic server error
  return {
    code: AuthErrorCode.SERVER_ERROR,
    message: 'An unexpected error occurred. Please try again.',
  };
};

/**
 * Get default error message for an error code
 */
const getDefaultMessage = (code: AuthErrorCode): string => {
  switch (code) {
    case AuthErrorCode.INVALID_CREDENTIALS:
      return 'Invalid email or password. Please try again.';
    case AuthErrorCode.ACCOUNT_LOCKED:
      return 'Your account has been temporarily locked. Please try again later.';
    case AuthErrorCode.ACCOUNT_DISABLED:
      return 'Your account has been disabled. Please contact your administrator.';
    case AuthErrorCode.RATE_LIMITED:
      return 'Too many login attempts. Please try again later.';
    case AuthErrorCode.NETWORK_ERROR:
      return 'Unable to connect. Please check your internet connection.';
    case AuthErrorCode.SERVER_ERROR:
      return 'Something went wrong. Please try again later or contact support.';
    case AuthErrorCode.SESSION_EXPIRED:
      return 'Your session has expired. Please sign in again.';
    case AuthErrorCode.EMAIL_NOT_VERIFIED:
      return 'Please verify your email address before signing in.';
    default:
      return 'An error occurred. Please try again.';
  }
};
